/**
 * Shared per-table column persistence helpers used by the account-dashboard
 * sub-tabs (Quotes, Invoices, …).
 *
 * Storage convention — kept identical to the prior per-tab code so existing
 * users keep their saved state on first deploy:
 *
 *   - Column visibility: one shared key per tab           e.g. `atek-account-quotes-column-visibility`
 *     holding `Record<columnKey, boolean>`.
 *   - Column filters: one key per (tab, accountId)        e.g. `atek-account-quotes-column-filters-{accountId}`
 *     holding `Record<columnKey, string>`.
 *
 * Kept as plain functions (not a hook) so the load happens inside the
 * `useState` lazy initializer and avoids a flash-of-default render.
 *
 * Related skill: `frontend-react-layout` §"localStorage key migration" —
 * never silently reset user state when refactoring persistence.
 */

export function loadColumnVisibility(
  storageKey: string,
  defaults: Record<string, boolean>,
): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return defaults;
}

export function saveColumnVisibility(
  storageKey: string,
  visibility: Record<string, boolean>,
): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  } catch {
    /* ignore */
  }
}

export function loadColumnFilters(
  keyPrefix: string,
  accountId: string,
): Record<string, string> {
  try {
    const stored = localStorage.getItem(`${keyPrefix}-${accountId}`);
    if (stored) return JSON.parse(stored);
  } catch {
    /* ignore */
  }
  return {};
}

export function saveColumnFilters(
  keyPrefix: string,
  accountId: string,
  filters: Record<string, string>,
): void {
  try {
    localStorage.setItem(`${keyPrefix}-${accountId}`, JSON.stringify(filters));
  } catch {
    /* ignore */
  }
}
